/**
 * DeveloperSettingsPage (개발자 설정 페이지)
 *
 * 개발 및 테스트를 위한 설정을 관리하는 페이지
 * 개발 모드(VITE_ENV=development)에서만 접근 가능
 *
 * 책임:
 * - Mock 모드 설정 및 전환
 * - 스프레드시트 초기화 도구
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isMockMode, toggleMockMode, env } from '@/core/config/env'
import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'
import { useWorkspaceStore } from '@/core/workspace/workspaceStore'
import { useAuth } from '@/core/auth/useAuth'
import { InitializationModal } from '@/shared/components/InitializationModal'
import { useTransactions } from '@/domains/transaction/hooks/useTransactions'
import type { InitMode } from '@/core/services/SpreadsheetInitializer'

export function DeveloperSettingsPage() {
  const navigate = useNavigate()
  const { activeWorkspaceId } = useWorkspaceStore()
  const { user } = useAuth()

  // 초기화 모달 상태
  const [showInitModal, setShowInitModal] = useState(false)

  // 초기화 모드 (env에서 가져오거나 기본값 사용)
  const initMode: InitMode = (env.INIT_MODE as InitMode) || 'production'

  // 성능 모니터링: 전체 거래 건수 조회
  const { transactions, loading: loadingTransactions } = useTransactions()

  /**
   * 거래 건수에 따른 최적화 권장사항 반환
   */
  const getPerformanceRecommendation = (count: number): { text: string; color: string } => {
    if (count < 1000) {
      return {
        text: '✅ 성능 양호. 최적화 불필요.',
        color: 'text-green-700 dark:text-green-300'
      }
    } else if (count >= 1000 && count < 3000) {
      return {
        text: '⚠️ Phase 1 캐싱 도입을 고려하세요.',
        color: 'text-yellow-700 dark:text-yellow-300'
      }
    } else if (count >= 3000) {
      return {
        text: '🚨 Phase 1 필수, Phase 2 권장.',
        color: 'text-red-700 dark:text-red-300'
      }
    }
    return {
      text: '모니터링 계속',
      color: 'text-gray-700 dark:text-gray-300'
    }
  }

  /**
   * Mock 모드 토글 핸들러
   */
  const handleMockToggle = () => {
    if (confirm('Mock 모드를 변경하면 페이지가 새로고침됩니다. 계속하시겠습니까?')) {
      toggleMockMode()
    }
  }

  /**
   * 스프레드시트 초기화 모달 열기
   */
  const handleOpenInitModal = () => {
    setShowInitModal(true)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="px-4 py-6 max-w-4xl mx-auto">
        {/* 페이지 헤더 */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 mb-2 flex items-center gap-1"
          >
            ← 뒤로 가기
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">개발자 설정</h1>
            <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200 text-sm font-medium rounded">
              개발 전용
            </span>
          </div>
        </div>

        {/* 개발자 설정 */}
        <div className="space-y-4">
          {/* Mock 데이터 모드 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-2 border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Mock 데이터 모드
                  </h3>
                  <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200 text-xs font-medium rounded">
                    개발자 전용
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                  Google Sheets 대신 로컬 Mock 데이터로 테스트
                </p>
                {isMockMode() && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                    ✓ 현재: Mock 모드 활성화됨
                  </p>
                )}
              </div>
              <button
                onClick={handleMockToggle}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors font-medium"
              >
                전환
              </button>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                💡 전환 후 페이지가 자동으로 새로고침됩니다
              </p>
            </div>
          </div>

          {/* 워크스페이스 경고 메시지 */}
          {!activeWorkspaceId && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                ⚠️ <strong>워크스페이스가 설정되지 않았습니다.</strong>
                {' '}스프레드시트 초기화 기능을 사용하려면 먼저 워크스페이스를 설정해주세요.
              </p>
            </div>
          )}

          {/* 스프레드시트 초기화 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-2 border-orange-200 dark:border-orange-800">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    스프레드시트 초기화
                  </h3>
                  <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-200 text-xs font-medium rounded">
                    개발자 전용
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Google Sheets 스프레드시트와 필요한 시트들을 자동으로 생성하고
                  기본 데이터를 초기화합니다.
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">현재 모드:</span>
                  <span
                    className={`px-2 py-0.5 rounded font-medium ${
                      initMode === 'test'
                        ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200'
                        : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200'
                    }`}
                  >
                    {initMode === 'test' ? 'Test (전체 데이터)' : 'Production (필수 데이터)'}
                  </span>
                </div>
              </div>
              <button
                onClick={handleOpenInitModal}
                disabled={!activeWorkspaceId}
                className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                  activeWorkspaceId
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                초기화 시작
              </button>
            </div>

            {/* 기능 설명 */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                이 도구는 다음 작업을 수행합니다:
              </p>
              <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 list-disc list-inside">
                <li>새 스프레드시트 생성 또는 기존 스프레드시트에 시트 추가</li>
                <li>5개 시트 생성: Categories, PaymentMethods, Users, Budgets, Transactions</li>
                <li>기본 데이터 삽입 (모드에 따라 다름)</li>
                <li>기존 시트는 자동으로 백업됨 (시트명_backup_타임스탬프)</li>
              </ul>
            </div>
          </div>

          {/* 성능 모니터링 대시보드 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-2 border-blue-200 dark:border-blue-800">
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  📊 성능 모니터링
                </h3>
                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 text-xs font-medium rounded">
                  개발자 전용
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Transaction 데이터 성능 및 최적화 권장사항
              </p>
            </div>

            {/* 성능 지표 */}
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">총 거래 건수</span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {loadingTransactions ? '...' : `${transactions.length}건`}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">로딩 시간</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  콘솔 로그 참조 →
                </span>
              </div>

              <div className="py-2">
                <span className="text-sm text-gray-600 dark:text-gray-400 block mb-1">권장 조치</span>
                <span className={`text-sm font-medium ${getPerformanceRecommendation(transactions.length).color}`}>
                  {getPerformanceRecommendation(transactions.length).text}
                </span>
              </div>
            </div>

            {/* 안내 메시지 */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                💡 브라우저 개발자 도구 콘솔에서 상세 성능 로그를 확인하세요.
                <br />
                (F12 키 → Console 탭 → "📊 Transactions loaded" 검색)
              </p>
            </div>

            {/* 최적화 단계 안내 */}
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-2 font-medium">
                최적화 단계:
              </p>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <li>• <strong>1,000건</strong> 도달 시: Phase 1 캐싱 검토</li>
                <li>• <strong>3,000건</strong> 도달 시: Phase 2 연도별 시트 분리</li>
                <li>• <strong>10,000건</strong> 도달 시: Phase 3 하이브리드 검토</li>
              </ul>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                📄 자세한 내용: <code>docs/PERFORMANCE_STRATEGY.md</code>
              </p>
            </div>
          </div>

          {/* 개발자 설정 안내 */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ <strong>주의:</strong> 개발자 설정은 개발 및 테스트 목적으로만 사용하세요.
              프로덕션 환경에서는 표시되지 않습니다.
            </p>
          </div>
        </div>
      </div>

      {/* 초기화 모달 - 워크스페이스가 있을 때만 렌더링 */}
      {showInitModal && activeWorkspaceId && (() => {
        try {
          const client = getWorkspaceClient()
          return (
            <InitializationModal
              isOpen={true}
              onClose={() => setShowInitModal(false)}
              client={client}
              mode={initMode}
              currentUser={user}
            />
          )
        } catch (error) {
          console.error('워크스페이스 클라이언트 로드 실패:', error)
          return null
        }
      })()}
    </div>
  )
}
